import { useQuery } from "@tanstack/react-query";
import { DocumentReference, FirestoreError } from "firebase/firestore";
import useUserStore from "@/modules/users/store";
import { getDocumentReferenceFromPath } from "@/utils/firestore";
import { getColaborador, getColaboradorFromDocument, colaboradorConverter } from "../firestore";

type GetColaboradorResponse = Awaited<ReturnType<typeof getColaborador>>;

/**
 * Get all colaboradores from reference path or document reference
 * @param colaboradoresReference Reference path or document reference
 * @returns All colaboradores
 */
function useGetColaboradoresFromReference(colaboradoresReference: string[] | DocumentReference<TColaborador>[]) {
  const activeEmpresaId = useUserStore((store) => store.activeEmpresaId);

  return useQuery<GetColaboradorResponse[], FirestoreError, TColaborador[]>({
    queryKey: ["empresas", { id: activeEmpresaId }, "colaboradores", { colaboradores: colaboradoresReference }],
    queryFn() {
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }
      if (!colaboradoresReference || colaboradoresReference.length === 0) {
        // eslint-disable-next-line compat/compat
        return Promise.resolve([]);
      }
      // eslint-disable-next-line compat/compat
      return Promise.all(
        colaboradoresReference
          .filter((reference) => reference != null) // Remove null/undefined
          .map((reference) => {
            if (typeof reference === "string" && reference.trim() !== "") {
              const colaboradorDocumentReference = getDocumentReferenceFromPath(reference, colaboradorConverter);
              return getColaborador(activeEmpresaId, colaboradorDocumentReference.id);
            }
            if (reference && typeof reference === "object" && "_delegate" in reference) {
              return getColaboradorFromDocument(reference);
            }
            // Se chegou aqui, a referência é inválida - retorna um documento que não existe
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, compat/compat
            return Promise.resolve({
              exists: () => false,
              data: () => null
            } as unknown as GetColaboradorResponse);
          })
      );
    },
    select(data) {
      const allColaboradores: TColaborador[] = [];
      const seenIds = new Set<string>();

      for (const colaborador of data) {
        if (colaborador.exists()) {
          const colaboradorData = colaborador.data();
          // Evita duplicatas baseado no ID
          if (!seenIds.has(colaboradorData.id)) {
            seenIds.add(colaboradorData.id);
            allColaboradores.push(colaboradorData);
          }
        }
      }
      return allColaboradores;
    }
  });
}

export default useGetColaboradoresFromReference;
